import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useFadeIn } from '../components/useFadeIn';
import MaxSong from '../Images/MaxSong.jpg';
import LiuYing from '../Images/LiuYing.jpg';
import Jason from '../Images/Jason.png';
import LiuYufan from '../Images/LiuYufan.jpg';
import AboveFooter from '../Images/abovefooter.jpg';

function FadeSection({ children, className = '' }) {
  const ref = useFadeIn();
  return <div ref={ref} className={`section-fade ${className}`}>{children}</div>;
}

const teamMembers = [
  {
    name: 'Max Song',
    role: 'Founder & CEO',
    bio: 'Max is a serial entrepreneur, investor, and changemaker at the intersection of sustainability, AI, and blockchain. A Forbes 30 Under 30, Hurun Under 30, and Tatler Gen T honoree, he is also a Schwarzman Scholar. With a deep focus on emerging technologies and their role in sustainability, Max leads Carbonetics in driving innovation for a low-carbon future.',
    photo: MaxSong,
  },
  {
    name: 'Liu Ying',
    role: 'Chief Operating Officer (COO)',
    bio: "Liu is the driving force behind Carbonetics growth and execution. With expertise in operations, finance, and sustainability, she ensures that the company's vision becomes reality, scaling efficiently while delivering real-world impact. She excels at bridging strategy and execution, integrating technology and operations to build scalable, sustainable businesses.",
    photo: LiuYing,
  },
  {
    name: 'Xiangzheng Zhang (Jason)',
    role: 'Chief Product Officer (CPO)',
    bio: "Jason is an expert in digital trade, green shipping, and blockchain solutions. With experience in energy, chemicals, and maritime industries, he drives Carbonetics' technological advancements to increase efficiency and competitiveness in international trade. His ability to integrate digital transformation with sustainability goals makes him a leader in the shift toward a low-carbon economy.",
    photo: Jason,
  },
  {
    name: 'Liu Yufan (Ivy)',
    role: 'Chief Financial Officer (CFO)',
    bio: "Ivy is a finance leader specializing in investment, capital management, and risk control. At Carbonetics, she ensures financial stability, regulatory compliance, and sustainable expansion. With a sharp focus on optimizing resources and mitigating risks, she plays a key role in maintaining Carbonetics' financial strength while enabling long-term growth.",
    photo: LiuYufan,
  },
];

export default function Team() {
  return (
    <main>
      {/* Hero */}
      <section
        className="bg-[#f4f7f0] py-32 sm:py-40 px-4 sm:px-6"
        data-navbar-light="true"
      >
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#1e3019] mb-4 sm:mb-6">
              The Minds Engineering a Sustainable Future
            </h1>
            <p className="font-body text-[#1e3019]/60 text-sm sm:text-base leading-relaxed max-w-xl mb-6 sm:mb-8">
              At Carbonetics, we go beyond tracking emissions—we redefine sustainability. Combining AI, finance, and digital transformation, we help industries turn carbon strategy into a competitive advantage.
            </p>
            <Link to="#members"
              className="inline-flex items-center gap-2 border border-[#1e3019] text-[#1e3019] font-body px-5 sm:px-6 py-2.5 sm:py-3 rounded-full hover:bg-[#1e3019] hover:text-white transition-all text-xs sm:text-sm">
              Explore Our Team <ArrowRight size={14} />
            </Link>
          </FadeSection>
        </div>
      </section>

      {/* Team heading */}
      <section className="bg-[#f4f7f0] py-4 px-4 sm:px-6" data-navbar-light="true">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1e3019]" id="members">The team</h2>
        </div>
      </section>

      {/* Team Members */}
      <section className="bg-[#f4f7f0] py-8 sm:py-12 px-4 sm:px-6" data-navbar-light="true">
        <div className="max-w-7xl mx-auto flex flex-col gap-0">
          {teamMembers.map((member, i) => (
            <FadeSection key={i}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center py-12 sm:py-16 border-b border-[#1e3019]/10">
                <div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#1e3019] mb-1 sm:mb-2">{member.name}</h3>
                  <p className="font-body text-[#1e3019]/50 text-xs sm:text-sm mb-6 sm:mb-8">{member.role}</p>
                  <p className="font-body text-[#1e3019]/60 text-xs sm:text-sm leading-relaxed">{member.bio}</p>
                </div>
                <div className="flex justify-center md:justify-end">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-48 h-60 sm:w-64 sm:h-80 object-contain bg-[#e8ede4] rounded-xl"
                  />
                </div>
              </div>
            </FadeSection>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#4cd964] py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-lg">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1e3019] mb-4 sm:mb-6">
              Take Action for a Greener Future
            </h2>
            <p className="font-body text-[#1e3019]/70 text-sm sm:text-base mb-8 sm:mb-12">
              Carbonetics helps businesses stay ahead with AI-driven sustainability solutions. Reduce emissions, optimize operations, and drive profitability with data-backed insights.
            </p>
            <Link to="/contact"
              className="inline-flex items-center gap-2 border border-[#1e3019] text-[#1e3019] font-body px-5 sm:px-6 py-2.5 sm:py-3 rounded-full hover:bg-[#1e3019] hover:text-white transition-all text-xs sm:text-sm">
              Get Started <ArrowRight size={14} />
            </Link>
          </div>
          <div className="shrink-0">
            <img
              src={AboveFooter}
              alt="Greener Future"
              className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full object-cover"
            />
          </div>
        </div>
      </section>
    </main>
  );
}